import React from "react";
import { Box } from "@mui/material";
import { Offer, OfferStatusCode } from "../models/models";
import { dt } from "../../../shared/themes/redesignTokens";
import ActiveWorkCard, { columnThemes } from "./ActiveWorkCard";

// ════════════════════════════════════════════════════════════════
//  Column config
// ════════════════════════════════════════════════════════════════

export interface ColumnConfig {
  statusCode: OfferStatusCode;
  titleKey: string;
  emptyKey: string;
}

export const KANBAN_COLUMNS: ColumnConfig[] = [
  {
    statusCode: OfferStatusCode.awaitingConfirmation,
    titleKey: "activeWork.colNeedsConfirmation",
    emptyKey: "activeWork.emptyNeedsConfirmation",
  },
  {
    statusCode: OfferStatusCode.confirmed,
    titleKey: "activeWork.colConfirmed",
    emptyKey: "activeWork.emptyConfirmed",
  },
  {
    statusCode: OfferStatusCode.workInProgress,
    titleKey: "activeWork.colInProgress",
    emptyKey: "activeWork.emptyInProgress",
  },
  {
    statusCode: OfferStatusCode.workCompleted,
    titleKey: "activeWork.colAwaitingCustomer",
    emptyKey: "activeWork.emptyAwaitingCustomer",
  },
];

// ════════════════════════════════════════════════════════════════
//  Props
// ════════════════════════════════════════════════════════════════

interface ActiveWorkColumnProps {
  config: ColumnConfig;
  offers: Offer[];
  translate: (key: string, options?: any) => any;
  onConfirmService: (offer: Offer) => void;
  onStartWork: (offer: Offer) => void;
  onMarkComplete: (offer: Offer) => void;
  onViewOffer: (offer: Offer) => void;
}

// ════════════════════════════════════════════════════════════════
//  ActiveWorkColumn
// ════════════════════════════════════════════════════════════════

const ActiveWorkColumn: React.FC<ActiveWorkColumnProps> = ({
  config,
  offers,
  translate,
  onConfirmService,
  onStartWork,
  onMarkComplete,
  onViewOffer,
}) => {
  const theme = columnThemes[config.statusCode] || columnThemes[OfferStatusCode.workInProgress];
  const count = offers.length;

  return (
    <Box
      sx={{
        flex: 1,
        minWidth: 0,
        display: "flex",
        flexDirection: "column",
        backgroundColor: theme.bg,
        borderRadius: "14px",
        border: `1px solid ${dt.cardBorder}`,
        overflow: "hidden",
        boxShadow: "0 1px 3px rgba(0,0,0,0.03)",
        animation: "fadeUp 0.5s cubic-bezier(0.4, 0, 0.2, 1) 0.1s both",
      }}
    >
      {/* ── Column header ── */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "14px 16px",
          borderBottom: `1px solid ${dt.cardBorder}`,
          backgroundColor: "rgba(255,255,255,0.6)",
          backdropFilter: "blur(6px)",
          position: "sticky",
          top: 0,
          zIndex: 1,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
          {/* Color dot */}
          <Box
            sx={{
              width: "8px",
              height: "8px",
              borderRadius: "50%",
              backgroundColor: theme.accent,
              boxShadow: `0 0 0 3px ${theme.glow}`,
            }}
          />
          <Box
            sx={{
              fontSize: "13px",
              fontWeight: 700,
              fontFamily: dt.font,
              color: dt.textPrimary,
              letterSpacing: "0.1px",
            }}
          >
            {translate(config.titleKey)}
          </Box>
        </Box>

        {/* Count badge */}
        <Box
          sx={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            minWidth: "22px",
            height: "22px",
            borderRadius: "11px",
            backgroundColor: count > 0 ? theme.accent : dt.inputBg,
            color: count > 0 ? "#FFFFFF" : dt.textMuted,
            fontSize: "11px",
            fontWeight: 700,
            fontFamily: dt.fontMono,
            padding: "0 6px",
            transition: "all 0.3s ease",
          }}
        >
          {count}
        </Box>
      </Box>

      {/* ── Card list ── */}
      <Box
        sx={{
          flex: 1,
          padding: "12px",
          display: "flex",
          flexDirection: "column",
          gap: "10px",
          overflowY: "auto",
          minHeight: "120px",
          maxHeight: { xs: "none", md: "calc(100vh - 280px)" },
          "&::-webkit-scrollbar": { width: "4px" },
          "&::-webkit-scrollbar-track": { backgroundColor: "transparent" },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: dt.cardBorder,
            borderRadius: "2px",
            "&:hover": { backgroundColor: dt.textMuted },
          },
        }}
      >
        {count > 0 ? (
          offers.map((offer, index) => (
            <ActiveWorkCard
              key={offer.id}
              offer={offer}
              index={index}
              translate={translate}
              onConfirmService={onConfirmService}
              onStartWork={onStartWork}
              onMarkComplete={onMarkComplete}
              onViewOffer={onViewOffer}
            />
          ))
        ) : (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flex: 1,
              minHeight: "80px",
              color: dt.textMuted,
              fontSize: "12px",
              fontFamily: dt.font,
              fontWeight: 400,
              textAlign: "center",
              padding: "16px",
            }}
          >
            {translate(config.emptyKey)}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default ActiveWorkColumn;
